/**
 * AuthContext.js - Session & Authentication Hardening
 * 
 * Implements:
 * - Centralized auth state management
 * - Silent token refresh (proactive at T-2min before expiry)
 * - Event-based re-auth prompts (no hard redirects)
 * - Multi-tab session persistence
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

// Token refresh timing constants
const REFRESH_BUFFER_MS = 2 * 60 * 1000; // 2 minutes before expiry
const TOKEN_CHECK_INTERVAL_MS = 30 * 1000; // Check every 30 seconds

// Event for cross-tab communication
const AUTH_STORAGE_KEY = 'simpliview_auth_event';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [reauthReason, setReauthReason] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const isInitialLoadRef = useRef(true); // Track if this is initial page load

  // Parse expiration from ISO string
  const parseExpiry = (expiresAtStr) => {
    if (!expiresAtStr) return null;
    const date = new Date(expiresAtStr);
    return isNaN(date.getTime()) ? null : date;
  };

  // Calculate time until expiry
  const getTimeUntilExpiry = useCallback(() => {
    if (!expiresAt) return null;
    return expiresAt.getTime() - Date.now();
  }, [expiresAt]);

  // Perform silent token refresh
  const refresh = useCallback(async (fromInitialLoad = false) => {
    if (isRefreshingRef.current) {
      console.log('[Auth] Refresh already in progress, skipping');
      return false;
    }

    isRefreshingRef.current = true;
    console.log('[Auth] Attempting silent token refresh...');

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Important: Include httpOnly cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorType = response.headers.get('X-Auth-Error') || errorData.detail || 'unknown';
        console.warn('[Auth] Refresh failed:', errorType);
        
        if (response.status === 401) {
          // Session truly expired
          if (fromInitialLoad || isInitialLoadRef.current) {
            // On initial page load - clear credentials and redirect to login
            // (ProtectedRoute will handle the redirect)
            console.log('[Auth] Session expired on initial load - clearing credentials');
            localStorage.removeItem('simpliview_token');
            localStorage.removeItem('simpliview_user');
            localStorage.removeItem('simpliview_token_expiry');
            localStorage.removeItem('simpliview_user_tenant_id');
            setUser(null);
            setIsAuthenticated(false);
            setExpiresAt(null);
          } else {
            // Active session expired while user is working - show re-auth modal
            console.log('[Auth] Session expired while working - showing re-auth modal');
            setReauthReason(errorType);
            setShowReauthModal(true);
          }
          return false;
        }
        
        // Transient error - will retry on next interval
        return false;
      }

      const data = await response.json();
      
      // Update token and expiry
      localStorage.setItem('simpliview_token', data.token);
      const newExpiry = parseExpiry(data.access_token_expires_at);
      setExpiresAt(newExpiry);
      
      // Broadcast to other tabs
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({
        type: 'refresh_success',
        token: data.token,
        expiresAt: data.access_token_expires_at,
        timestamp: Date.now()
      }));
      
      console.log('[Auth] Token refreshed successfully, expires:', data.access_token_expires_at);
      return true;
    } catch (error) {
      console.error('[Auth] Refresh error:', error);
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  // Schedule next refresh
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const timeUntilExpiry = getTimeUntilExpiry();
    if (timeUntilExpiry === null || timeUntilExpiry <= 0) {
      console.log('[Auth] Token already expired or no expiry set');
      return;
    }

    // Schedule refresh 2 minutes before expiry
    const refreshIn = Math.max(timeUntilExpiry - REFRESH_BUFFER_MS, 1000);
    
    console.log(`[Auth] Scheduling refresh in ${Math.round(refreshIn / 1000)}s (expiry in ${Math.round(timeUntilExpiry / 1000)}s)`);
    
    refreshTimerRef.current = setTimeout(async () => {
      const success = await refresh();
      if (success) {
        // Schedule next refresh after successful refresh
        scheduleRefresh();
      }
    }, refreshIn);
  }, [getTimeUntilExpiry, refresh]);

  // Initialize auth state from storage
  useEffect(() => {
    const token = localStorage.getItem('simpliview_token');
    const storedUser = localStorage.getItem('simpliview_user');
    const storedExpiry = localStorage.getItem('simpliview_token_expiry');
    
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAuthenticated(true);
        
        // TENANT ISOLATION: Ensure tenant_id is stored for API filtering
        const userTenantId = userData.tenant_id || (userData.tenant_ids && userData.tenant_ids[0]);
        if (userTenantId && !localStorage.getItem('simpliview_user_tenant_id')) {
          localStorage.setItem('simpliview_user_tenant_id', userTenantId);
        }
        
        if (storedExpiry) {
          const expiry = parseExpiry(storedExpiry);
          if (expiry && expiry.getTime() > Date.now()) {
            setExpiresAt(expiry);
            // Token is still valid - mark initial load complete
            isInitialLoadRef.current = false;
          } else {
            // Token expired on initial load - attempt refresh with flag
            refresh(true).finally(() => {
              isInitialLoadRef.current = false;
            });
          }
        } else {
          // No expiry stored - mark initial load complete
          isInitialLoadRef.current = false;
        }
      } catch (e) {
        console.error('[Auth] Failed to parse stored user:', e);
        localStorage.removeItem('simpliview_token');
        localStorage.removeItem('simpliview_user');
        localStorage.removeItem('simpliview_token_expiry');
        localStorage.removeItem('simpliview_user_tenant_id');
        isInitialLoadRef.current = false;
      }
    } else {
      // No token/user - mark initial load complete
      isInitialLoadRef.current = false;
    }
    setLoading(false);
  }, [refresh]);

  // Schedule refresh when expiry changes
  useEffect(() => {
    if (isAuthenticated && expiresAt) {
      scheduleRefresh();
    }
    
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [isAuthenticated, expiresAt, scheduleRefresh]);

  // Cross-tab communication
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === AUTH_STORAGE_KEY && e.newValue) {
        try {
          const event = JSON.parse(e.newValue);
          
          if (event.type === 'refresh_success') {
            // Another tab refreshed - update our state
            localStorage.setItem('simpliview_token', event.token);
            setExpiresAt(parseExpiry(event.expiresAt));
            console.log('[Auth] Token synced from another tab');
          } else if (event.type === 'logout') {
            // Another tab logged out
            handleLogoutCleanup();
          }
        } catch (e) {
          console.error('[Auth] Failed to process storage event:', e);
        }
      }
      
      // Also listen for direct token changes
      if (e.key === 'simpliview_token' && !e.newValue && isAuthenticated) {
        // Token was removed (logout from another tab)
        handleLogoutCleanup();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAuthenticated]);

  // Cleanup function for logout
  const handleLogoutCleanup = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    setUser(null);
    setIsAuthenticated(false);
    setExpiresAt(null);
    setShowReauthModal(false);
  }, []);

  // Login function
  const login = async (email, password, rememberMe = false) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include', // Important: Allow httpOnly cookie to be set
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, error: data.detail || 'Login failed', mfa_required: data.mfa_required };
      }
      
      // Check if MFA is required
      if (data.mfa_required) {
        return {
          success: false,
          mfa_required: true,
          mfa_session_token: data.mfa_session_token,
          user_id: data.user_id,
        };
      }
      
      // Successful login - store token and user
      localStorage.setItem('simpliview_token', data.token);
      localStorage.setItem('simpliview_user', JSON.stringify(data.user));
      localStorage.setItem('simpliview_token_expiry', data.access_token_expires_at);
      
      // TENANT ISOLATION: Store user's tenant_id for API filtering
      // Support both single tenant_id and tenant_ids array
      const userTenantId = data.user.tenant_id || (data.user.tenant_ids && data.user.tenant_ids[0]);
      if (userTenantId) {
        localStorage.setItem('simpliview_user_tenant_id', userTenantId);
      }
      
      setUser(data.user);
      setIsAuthenticated(true);
      setExpiresAt(parseExpiry(data.access_token_expires_at));
      setShowReauthModal(false);
      isInitialLoadRef.current = false; // User actively logged in, no longer initial load
      
      // Set must change password flag for first-time login
      const needsPasswordChange = data.must_change_password || false;
      setMustChangePassword(needsPasswordChange);
      
      // Return must_change_password flag for first-time login with temp password
      return { 
        success: true, 
        user: data.user,
        must_change_password: needsPasswordChange
      };
    } catch (error) {
      console.error('[Auth] Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // MFA verification
  const verifyMFA = async (mfaSessionToken, userId, code, rememberDevice = false) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/auth/login/mfa-verify`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mfa_session_token: mfaSessionToken,
          user_id: userId,
          code,
          remember_device: rememberDevice,
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, error: data.detail || 'MFA verification failed' };
      }
      
      // Successful MFA - store token and user
      localStorage.setItem('simpliview_token', data.token);
      localStorage.setItem('simpliview_user', JSON.stringify(data.user));
      localStorage.setItem('simpliview_token_expiry', data.access_token_expires_at);
      
      // TENANT ISOLATION: Store user's tenant_id for API filtering
      const userTenantId = data.user.tenant_id || (data.user.tenant_ids && data.user.tenant_ids[0]);
      if (userTenantId) {
        localStorage.setItem('simpliview_user_tenant_id', userTenantId);
      }
      
      if (data.device_token) {
        localStorage.setItem('simpliview_device_token', data.device_token);
      }
      
      setUser(data.user);
      setIsAuthenticated(true);
      setExpiresAt(parseExpiry(data.access_token_expires_at));
      setShowReauthModal(false);
      
      return { success: true, user: data.user };
    } catch (error) {
      console.error('[Auth] MFA verify error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const token = localStorage.getItem('simpliview_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('[Auth] Logout API error:', error);
    } finally {
      // Always clean up local state
      localStorage.removeItem('simpliview_token');
      localStorage.removeItem('simpliview_user');
      localStorage.removeItem('simpliview_token_expiry');
      localStorage.removeItem('simpliview_user_tenant_id'); // TENANT ISOLATION: Clear user tenant
      localStorage.removeItem('impersonated_tenant_id');    // Also clear any impersonation
      localStorage.removeItem('impersonated_tenant_name');
      
      // Broadcast logout to other tabs
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({
        type: 'logout',
        timestamp: Date.now()
      }));
      
      handleLogoutCleanup();
    }
  };

  // Dismiss re-auth modal (user cancelled)
  const dismissReauthModal = useCallback(() => {
    setShowReauthModal(false);
    setReauthReason(null);
  }, []);

  // Re-authenticate from modal
  const reauthenticate = async (email, password) => {
    const result = await login(email, password);
    if (result.success) {
      setShowReauthModal(false);
      setReauthReason(null);
    }
    return result;
  };

  // Update profile
  const updateProfile = async (profileData) => {
    try {
      const token = localStorage.getItem('simpliview_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });
      
      if (!response.ok) {
        const data = await response.json();
        return { success: false, error: data.detail || 'Update failed' };
      }
      
      const updatedUser = { ...user, ...profileData };
      localStorage.setItem('simpliview_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // Get persona-based landing route
  // V1 READINESS: Updated to use Executive Hub as the unified entry point
  const getPersonaLanding = () => {
    if (!user || !user.roles || user.roles.length === 0) {
      return '/executive';
    }
    
    const primaryRole = user.roles[0]?.role_key;
    
    switch (primaryRole) {
      case 'sysadmin':
      case 'pmo_power':
      case 'executive':
        return '/executive';
      case 'program_admin':
        return '/dashboard/pmo';
      case 'pm':
        return '/dashboard/project-manager';
      case 'viewer':
        return '/executive';
      default:
        return '/executive';
    }
  };

  // Check if user has a specific permission
  const hasPermission = (permission, scopeType = 'global', scopeId = null) => {
    if (!user) return false;
    
    const rolePerms = user.permissions || [];
    if (rolePerms.includes(permission)) return true;
    
    const overrides = user.overrides || [];
    return overrides.some(o => 
      o.permission_key === permission &&
      (o.scope_type === 'global' || (o.scope_type === scopeType && o.scope_id === scopeId))
    );
  };

  // Check if user has any of the specified roles
  const hasRole = (...roleKeys) => {
    if (!user || !user.roles) return false;
    return user.roles.some(r => roleKeys.includes(r.role_key));
  };
  
  // TENANT ISOLATION: Helper methods for tenant-scoped operations
  
  // Get the active tenant ID (impersonated or user's actual)
  const getActiveTenantId = () => {
    const impersonated = localStorage.getItem('impersonated_tenant_id');
    return impersonated || user?.tenant_id || (user?.tenant_ids && user.tenant_ids[0]) || null;
  };
  
  // Check if user is a platform admin (sysadmin with global access)
  const isPlatformAdmin = () => {
    return user?.is_platform_admin === true || hasRole('sysadmin');
  };
  
  // Check if user has access to a specific tenant
  const hasTenantAccess = (targetTenantId) => {
    if (isPlatformAdmin()) return true; // Sysadmins can access all
    if (!targetTenantId) return false;
    
    // Check user's assigned tenants
    const userTenantId = user?.tenant_id;
    const userTenantIds = user?.tenant_ids || [];
    
    return userTenantId === targetTenantId || userTenantIds.includes(targetTenantId);
  };
  
  // Check if currently in impersonation mode
  const isImpersonating = () => {
    return !!localStorage.getItem('impersonated_tenant_id');
  };

  // Clear the must change password flag after successful password change
  const clearMustChangePassword = () => {
    setMustChangePassword(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    expiresAt,
    showReauthModal,
    reauthReason,
    mustChangePassword,
    login,
    verifyMFA,
    logout,
    refresh,
    updateProfile,
    getPersonaLanding,
    hasPermission,
    hasRole,
    dismissReauthModal,
    reauthenticate,
    clearMustChangePassword,
    tenantId: user?.tenant_id || user?.tenant?.id || 'default-tenant',
    // TENANT ISOLATION: New helpers
    getActiveTenantId,
    isPlatformAdmin,
    hasTenantAccess,
    isImpersonating,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
